import { SlideObject } from '../types';
import { catalogValueItems, FieldBinding, fieldBindingKey, isListBindingKey } from '../fieldCatalog';
import { upsertLinkedField } from './linkedFields';

export function formatBoundContent(existing: string | undefined, incoming: string, key?: string | null): string {
  const items = catalogValueItems(incoming);
  const kind = /<ol[\s>]/i.test(existing || '') ? 'ordered'
    : /<ul[\s>]/i.test(existing || '') ? 'bullet'
    : 'none';
  const asList = kind !== 'none' || items.length > 1 || isListBindingKey(key);
  if (!asList || !items.length) return incoming;
  const tag = kind === 'ordered' ? 'ol' : 'ul';
  return `<${tag}>${items.map((item) => `<li>${item}</li>`).join('')}</${tag}>`;
}

export function slideHasBindings(objects: SlideObject[]): boolean {
  return (objects || []).some((object) => (
    Boolean(object.fieldBinding)
    || object.slotId === 'product'
    || (object.type === 'product' && Boolean(object.slotId))
  ));
}

export function resolvePopulateTarget(
  objects: SlideObject[],
  selectedId?: string | null
): SlideObject | undefined {
  const selected = objects.find((object) => object.id === selectedId);
  if (selected && (selected.type === 'product' || selected.slotId === 'product')) return selected;
  const slot = objects.find((object) => object.slotId === 'product');
  if (slot) return slot;
  const products = objects.filter((object) => object.type === 'product');
  if (products.length === 1) return products[0];
  return undefined;
}

export function populateBoundSlide(
  objects: SlideObject[],
  productId: string,
  values: Record<string, string | undefined>,
  media?: { assetId?: string; assetVersionId?: string | null; imageUrl?: string }
): SlideObject[] {
  const product = objects.find((object) => object.id === productId);
  if (!product) return objects;

  let linkedFields = product.linkedFields || [];
  const next = objects.map((object) => {
    if (object.id === productId) return object;
    const key = fieldBindingKey(object.fieldBinding as FieldBinding | undefined);
    if (!key) return object;
    if (object.linkedFrom && object.linkedFrom.objectId !== productId) return object;
    const incoming = values[key];
    const content = incoming == null ? '' : formatBoundContent(object.content, incoming, key);
    const current = linkedFields.find((field) => field.key === key);
    if (!current || current.source !== 'local') {
      linkedFields = upsertLinkedField(linkedFields, {
        key,
        source: current?.source || 'direct',
        value: content,
      });
    }
    if (current?.source === 'local') return { ...object, linkedFrom: { objectId: productId, key } };
    return {
      ...object,
      linkedFrom: { objectId: productId, key },
      content,
    };
  });

  return next.map((object) => {
    if (object.id !== productId) return object;
    return {
      ...object,
      type: 'product' as const,
      assetId: media?.assetId ?? object.assetId,
      assetVersionId: media?.assetVersionId !== undefined ? media.assetVersionId : object.assetVersionId,
      imageUrl: media?.imageUrl !== undefined ? media.imageUrl : object.imageUrl,
      trackingMode: object.trackingMode || 'pin',
      linkedFields,
    };
  });
}
